import React, { Suspense } from 'react';

type DynamicScriptArgs = {
    url?: string;
};

type RemoteComponent<TProps> = React.ComponentType<TProps>;

// Local MF typings (avoid relying on consumer tsconfig include for .d.ts globals)
type WebpackModuleFactory<T = unknown> = () => T;

interface WebpackContainer {
    init(shareScope: unknown): Promise<void>;
    get<T = unknown>(module: string): Promise<WebpackModuleFactory<T>>;
}

declare const __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: Record<string, unknown>;

const getWebpackContainer = (scope: string): WebpackContainer => {
    const container = (window as unknown as Record<string, unknown>)[scope] as WebpackContainer | undefined;

    if (!container) {
        throw new Error(`Module Federation container not found on window["${scope}"]`);
    }

    return container;
};

const unwrapDefaultExport = <T,>(mod: unknown): T => {
    if (mod && typeof mod === 'object' && 'default' in mod) {
        return (mod as { default: T }).default;
    }
    return mod as T;
};

/* TODO This module loaders should be removed when the UFE product exports it. */
const useDynamicScript = (args: DynamicScriptArgs) => {
    const [ready, setReady] = React.useState(false);
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        if (!args.url) {
            return;
        }

        const element = document.createElement('script');

        element.src = args.url;
        element.type = 'text/javascript';
        element.async = true;

        setReady(false);
        setFailed(false);

        element.onload = () => {
            console.warn(`Dynamic Script Loaded: ${args.url}`);
            setReady(true);
        };

        element.onerror = () => {
            console.error(`Dynamic Script Error: ${args.url}`);
            setReady(false);
            setFailed(true);
        };

        document.head.appendChild(element);

        return () => {
            console.warn(`Dynamic Script Removed: ${args.url}`);
            document.head.removeChild(element);
        };
    }, [args.url]);

    return { ready, failed };
};

async function loadRemoteComponent<TProps extends object>(
    scope: string,
    module: string
): Promise<{ default: RemoteComponent<TProps> }> {
    await __webpack_init_sharing__('default');

    const container = getWebpackContainer(scope);

    await container.init(__webpack_share_scopes__.default);

    // In Module Federation, factory() often returns a module namespace object: { default: Component, ... }
    const factory = await container.get<unknown>(module);
    const loadedModule = factory();

    const Component = unwrapDefaultExport<RemoteComponent<TProps>>(loadedModule);

    return { default: Component };
}

type InnerProps<T> = Omit<T, 'ref' | 'key'>;

export interface BaseModuleLoaderProps<T extends object = Record<string, unknown>> {
    module: string;
    url: string;
    scope: string;
    innerProps?: InnerProps<T>;
    key: string;
    errorComponent?: React.ReactElement;
    loadingComponent?: React.ReactElement;
}

// TODO - Use/adapt foundations ModuleLoader.
const ModuleLoader = React.forwardRef(function Module<T extends object = Record<string, unknown>>(
    {
        module,
        url,
        scope,
        innerProps,
        errorComponent: ErrorComponent,
        loadingComponent: LoadingComponent
    }: BaseModuleLoaderProps<T>,
    ref: React.Ref<unknown>
) {
    const { ready, failed } = useDynamicScript({
        url: module && url ? url : undefined
    });

    const Component = React.useMemo(() => {
        if (!module || !url || !scope) return null;
        return React.lazy(() => loadRemoteComponent<InnerProps<T>>(scope, module));
    }, [module, url, scope]);

    if (!module || failed) {
        return ErrorComponent ?? null;
    }

    if (!ready) {
        return LoadingComponent ?? null;
    }

    if (!Component) return null;

    // React.lazy + generic props + ref hits @types/react overload limits.
    // Keep the cast localized to this one spot.
    const Remote = Component as unknown as React.ForwardRefExoticComponent<
        InnerProps<T> & React.RefAttributes<unknown>
    >;

    return (
        <Suspense fallback={LoadingComponent}>
            <Remote ref={ref} {...(innerProps ?? ({} as InnerProps<T>))} />
        </Suspense>
    );
}) as <T extends object = Record<string, unknown>>(
    props: BaseModuleLoaderProps<T> & React.RefAttributes<unknown>
) => React.ReactElement | null;

export default ModuleLoader;
